import React, { useEffect, useState } from 'react';
import { fetchSimilarDays, type SimilarDayData } from './api';
import type { FilterState } from './FilterPanel';

interface Props {
  instrument: string;
  onApplySimilarGap: (gapDir: 'up' | 'down', gapMin: number, gapMax: number) => void;
}

export default function SimilarDaysPanel({ instrument, onApplySimilarGap }: Props) {
  const [data, setData] = useState<SimilarDayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchSimilarDays(instrument).then(d => {
      setData(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [instrument]);

  if (loading) {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>⚡ Gap Edge</div>
        <div style={styles.skeleton}>
          <div style={{ ...styles.skelLine, width: '55%' }} />
          <div style={{ ...styles.skelLine, width: '75%' }} />
          <div style={{ ...styles.skelLine, width: '40%' }} />
        </div>
      </div>
    );
  }

  if (!data || data.similarCount < 5) {
    return (
      <div style={styles.panel}>
        <div style={styles.header}>⚡ Gap Edge</div>
        <div style={styles.empty}>Not enough historical data</div>
      </div>
    );
  }

  const gapDirColor = data.gapDirection === 'up' ? '#22c55e' : data.gapDirection === 'down' ? '#ef4444' : '#94a3b8';
  const closedColor = data.similarGapsClosedPct >= 70 ? '#22c55e' : data.similarGapsClosedPct >= 50 ? '#f59e0b' : '#ef4444';
  const gapMin = Math.max(0, data.gapPctAdr - 15);
  const gapMax = data.gapPctAdr + 15;

  const handleApply = () => {
    // Apply gap ADR range filter; direction may be null (no gap detected today)
    onApplySimilarGap(data.gapDirection ?? undefined, gapMin, gapMax);
  };

  return (
    <div style={styles.panel}>
      <div style={styles.header} onClick={() => setCollapsed(!collapsed)} className="clickable">
        <span>⚡ Gap Edge</span>
        <span style={{ color: gapDirColor, fontWeight: 700 }}>
          Gap {data.gapDirection?.toUpperCase()} · {data.gapPctAdr.toFixed(1)}% ADR · {data.similarCount} similar days
        </span>
        <span style={styles.collapseBtn}>{collapsed ? '▼' : '▲'}</span>
      </div>

      {!collapsed && (
        <div style={styles.body}>
          {/* Gap close stats */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Similar Gaps (±15%)</div>
            <div style={styles.gapCloseRow}>
              <span style={styles.metricLabel}>Gap Fill Rate</span>
              <div style={styles.pctBarWrap}>
                <div style={{ ...styles.pctBarFill, width: `${data.similarGapsClosedPct}%`, background: closedColor }} />
                <span style={styles.pctBarLabel}>{data.similarGapsClosedPct.toFixed(1)}%</span>
              </div>
            </div>
            <div style={styles.avgCandle}>
              Avg Fill Candle: <span style={{ color: '#3b82f6', fontWeight: 700 }}>#{data.avgGapCloseCandle}</span>
            </div>
          </div>

          {/* Apply filter button */}
          <div style={styles.applyRow}>
            <span style={styles.applyLabel}>Apply to ORB filters</span>
            <button style={styles.applyBtn} onClick={handleApply}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Use This Gap
            </button>
          </div>

          {/* Open position note */}
          {data.openPosition && (
            <div style={styles.openPosNote}>
              Open {data.openPosition.toUpperCase()} prev range · higher relevance signal
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  panel: {
    background: 'rgba(22,24,42,0.85)',
    borderRadius: 14,
    padding: '18px 22px',
    backdropFilter: 'blur(20px)',
  },
  header: {
    fontSize: 13,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    cursor: 'pointer',
  },
  collapseBtn: {
    fontSize: 11,
    color: '#475569',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  skeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '8px 0',
  },
  skelLine: {
    height: 16,
    background: '#334155',
    borderRadius: 4,
  },
  empty: {
    color: '#475569',
    fontSize: 13,
    textAlign: 'center',
    padding: '16px 0',
  },
  gapCloseRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  metricLabel: {
    fontSize: 12,
    color: '#94a3b8',
    width: 90,
    flexShrink: 0,
  },
  pctBarWrap: {
    position: 'relative',
    flex: 1,
    height: 20,
    background: '#334155',
    borderRadius: 100,
    overflow: 'hidden',
    maxWidth: 200,
  },
  pctBarFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    borderRadius: 100,
  },
  pctBarLabel: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
  },
  avgCandle: {
    fontSize: 12,
    color: '#94a3b8',
  },
  applyRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    background: 'rgba(245,158,11,0.06)',
    borderRadius: 10,
    border: '1px solid rgba(245,158,11,0.12)',
  },
  applyLabel: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 500,
  },
  applyBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    background: 'rgba(245,158,11,0.12)',
    border: '1px solid rgba(245,158,11,0.3)',
    borderRadius: 8,
    color: '#f59e0b',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.18s',
    letterSpacing: '0.02em',
  },
  applyIcon: {
  },
  openPosNote: {
    fontSize: 11,
    color: '#6b7280',
    fontStyle: 'italic',
    padding: '6px 10px',
    background: '#1e293b',
    borderRadius: 6,
  },
};